import React, { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import Waveform from "./Waveform";

/**
 * AudioPlayer Component
 * Reusable audio player with waveform visualization
 * Uses Web Audio API for iOS Safari compatibility
 *
 * @param {string} audioUrl - URL of the audio file
 * @param {Array<number>} waveformData - Pre-computed waveform data (1024 integers 0-255) from backend (optional)
 * @param {boolean} showStaticWaveform - Show static waveform when paused (default: true)
 * @param {function} onEnded - Callback when audio ends
 * @param {function} onClick - Callback when player is clicked (optional, for preventing navigation)
 * @param {boolean} loadImmediately - If true, load audio immediately instead of waiting for viewport (default: false)
 */
const AudioPlayer = ({
	audioUrl,
	waveformData = null, // Pre-computed waveform from backend
	showStaticWaveform = true,
	onEnded,
	onClick,
	loadImmediately = false,
}) => {
	console.log("AudioPlayer: Component rendered", {
		audioUrl,
		loadImmediately,
		hasAudioUrl: !!audioUrl,
	});

	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);
	const [audioLevels, setAudioLevels] = useState(null); // Start with null, will be set after waveform loads
	const [error, setError] = useState(null);
	const [isLoadingWaveform, setIsLoadingWaveform] = useState(false); // Track waveform loading state - start false, set true when actually loading

	const animationFrameRef = useRef(null);
	const visualizationFrameRef = useRef(null); // Separate ref for visualization
	const realWaveformRef = useRef(null); // Static waveform data
	const audioContextRef = useRef(null);
	const analyserRef = useRef(null);
	const audioBufferRef = useRef(null); // Store decoded audio buffer
	const arrayBufferRef = useRef(null); // Store raw array buffer for retry decode
	const sourceNodeRef = useRef(null); // Current AudioBufferSourceNode
	const gainNodeRef = useRef(null);
	const startTimeRef = useRef(0); // When playback started
	const pausedTimeRef = useRef(0); // Time when paused
	const isPlayingRef = useRef(false); // Track playing state for visualization closure
	const progressBarRef = useRef(null);
	const progressFillRef = useRef(null);
	const thumbRef = useRef(null);
	const containerRef = useRef(null);
	// Initialize isInViewport based on loadImmediately prop
	const [isInViewport, setIsInViewport] = useState(loadImmediately);

	// Intersection Observer to only load waveforms when in/near viewport
	// Skip if loadImmediately is true (for first page items)
	useEffect(() => {
		// If loadImmediately is true, ensure we're marked as in viewport and skip observer
		if (loadImmediately) {
			console.log("AudioPlayer: Loading immediately (first page):", audioUrl);
			// Set immediately - don't wait for container or observer
			setIsInViewport(true);
			return () => {}; // Return empty cleanup function
		}

		// For lazy loading, wait for container to be mounted
		if (!containerRef.current) {
			// Retry after a short delay if container isn't ready yet
			const timeoutId = setTimeout(() => {
				if (containerRef.current && !loadImmediately) {
					// Container is now available, set up observer
					const observer = new IntersectionObserver(
						(entries) => {
							entries.forEach((entry) => {
								console.log("AudioPlayer: Intersection observed", {
									isIntersecting: entry.isIntersecting,
									audioUrl,
								});
								if (entry.isIntersecting) {
									setIsInViewport(true);
									// Once in viewport, stop observing (we only need to load once)
									observer.unobserve(entry.target);
								}
							});
						},
						{
							// Load when within 50% of viewport height from being visible
							rootMargin: "50% 0px",
							threshold: 0,
						}
					);
					observer.observe(containerRef.current);
				}
			}, 100);
			return () => clearTimeout(timeoutId);
		}

		// Container is available, set up observer for lazy loading
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					console.log("AudioPlayer: Intersection observed", {
						isIntersecting: entry.isIntersecting,
						audioUrl,
					});
					if (entry.isIntersecting) {
						setIsInViewport(true);
						// Once in viewport, stop observing (we only need to load once)
						observer.unobserve(entry.target);
					}
				});
			},
			{
				// Load when within 50% of viewport height from being visible
				rootMargin: "50% 0px",
				threshold: 0,
			}
		);

		observer.observe(containerRef.current);

		return () => {
			observer.disconnect();
		};
	}, [audioUrl, loadImmediately]);

	// Use pre-computed waveform data from backend if available
	useEffect(() => {
		if (
			waveformData &&
			Array.isArray(waveformData) &&
			waveformData.length === 1024
		) {
			console.log("AudioPlayer: Using pre-computed waveform from backend");
			// Validate waveform data (should be 0-255)
			const validWaveform = waveformData.map((val) => {
				const num = typeof val === "number" ? val : parseInt(val, 10);
				return Math.max(0, Math.min(255, isNaN(num) ? 128 : num));
			});
			realWaveformRef.current = validWaveform;
			setAudioLevels(validWaveform);
			setIsLoadingWaveform(false);
			console.log(
				"AudioPlayer: Pre-computed waveform loaded, length:",
				validWaveform.length
			);
		}
	}, [waveformData]);

	// Load audio and decode to AudioBuffer (only if waveformData not provided)
	useEffect(() => {
		console.log("AudioPlayer: Load effect triggered", {
			isInViewport,
			loadImmediately,
			audioUrl,
			hasAudioUrl: !!audioUrl,
			hasWaveformData: !!waveformData,
		});

		// Only load if in viewport (or approaching it) OR if loadImmediately is true
		if (!isInViewport && !loadImmediately) {
			console.log("AudioPlayer: Skipping load, not in viewport yet:", audioUrl);
			// Don't show loading spinner if not loading yet
			setIsLoadingWaveform(false);
			return;
		}

		if (!audioUrl) {
			console.log("AudioPlayer: No audioUrl provided");
			// No audio URL means no loading needed
			setIsLoadingWaveform(false);
			return;
		}

		// If we have pre-computed waveform, we still need to load audio for playback
		// but we can skip the waveform extraction
		const needsWaveformExtraction =
			!waveformData ||
			!Array.isArray(waveformData) ||
			waveformData.length !== 1024;

		const loadAudio = async () => {
			try {
				// Only reset loading state if we need to extract waveform
				if (needsWaveformExtraction) {
					setIsLoadingWaveform(true);
					setAudioLevels(null);
				}
				console.log("AudioPlayer: Starting loadAudio function for:", audioUrl, {
					loadImmediately,
					isInViewport,
				});

				// CRITICAL: Use HTML5 audio element as fallback to get duration (iOS Safari compatibility)
				const tempAudio = new Audio(audioUrl);
				tempAudio.preload = "metadata";
				tempAudio.crossOrigin = "anonymous";

				// Wait for metadata to load to get duration
				const durationPromise = new Promise((resolve) => {
					let resolved = false;
					let timeoutId = null;

					const cleanup = (onLoadedMetadata, onError) => {
						tempAudio.removeEventListener("loadedmetadata", onLoadedMetadata);
						tempAudio.removeEventListener("error", onError);
						if (timeoutId) {
							clearTimeout(timeoutId);
						}
					};

					const onError = (e) => {
						if (resolved) {
							return;
						}
						resolved = true;
						console.error("AudioPlayer: HTML5 audio error:", e);
						cleanup(onLoadedMetadata, onError);
						resolve(0);
					};

					const onLoadedMetadata = () => {
						if (resolved) {
							return;
						}
						resolved = true;
						const html5Duration = tempAudio.duration;
						console.log(
							"AudioPlayer: HTML5 audio duration:",
							html5Duration,
							"isFinite:",
							isFinite(html5Duration),
							"isNaN:",
							isNaN(html5Duration)
						);
						cleanup(onLoadedMetadata, onError);
						resolve(html5Duration);
					};

					tempAudio.addEventListener("loadedmetadata", onLoadedMetadata);
					tempAudio.addEventListener("error", onError);

					// Timeout after 5 seconds
					timeoutId = setTimeout(() => {
						if (resolved) {
							return;
						}
						resolved = true;
						console.warn("AudioPlayer: HTML5 audio duration timeout");
						cleanup(onLoadedMetadata, onError);
						resolve(0);
					}, 5000);
				});

				// Fetch audio data
				console.log("AudioPlayer: Fetching audio data...");
				const response = await fetch(audioUrl, {
					mode: "cors",
					credentials: "omit",
				});
				if (!response.ok) {
					throw new Error(
						`Failed to fetch audio: ${response.status} ${response.statusText}`
					);
				}
				console.log("AudioPlayer: Fetch successful, reading array buffer...");
				const arrayBuffer = await response.arrayBuffer();
				arrayBufferRef.current = arrayBuffer; // Store for potential retry decode
				console.log("AudioPlayer: Array buffer size:", arrayBuffer.byteLength);

				// Create or get AudioContext
				if (!audioContextRef.current) {
					audioContextRef.current = new (
						window.AudioContext || window.webkitAudioContext
					)();
				}

				const ctx = audioContextRef.current;

				// CRITICAL: Resume AudioContext if suspended (required for iOS Safari)
				// For immediate loading, try to resume but don't block if it fails
				if (ctx.state === "suspended") {
					try {
						// Try to resume, but don't await if loadImmediately (non-blocking)
						if (loadImmediately) {
							// For immediate loads, try resume but continue even if it fails
							ctx.resume().catch((resumeError) => {
								console.warn(
									"AudioPlayer: Could not resume AudioContext during immediate load (will resume on play):",
									resumeError
								);
							});
							console.log(
								"AudioPlayer: AudioContext resume attempted (non-blocking for immediate load)"
							);
						} else {
							// For lazy loads, await resume (blocking)
							await ctx.resume();
							console.log("AudioPlayer: AudioContext resumed during load");
						}
					} catch (resumeError) {
						console.warn(
							"AudioPlayer: Could not resume AudioContext during load:",
							resumeError
						);
					}
				}

				// Decode audio data
				let audioBuffer;
				try {
					console.log("AudioPlayer: Decoding audio data...", {
						arrayBufferSize: arrayBuffer.byteLength,
						ctxState: ctx.state,
						loadImmediately,
					});

					// On iOS Safari, decodeAudioData might require AudioContext to be running
					// If suspended and loadImmediately, try to resume first
					if (ctx.state === "suspended" && loadImmediately) {
						console.log(
							"AudioPlayer: AudioContext suspended, attempting resume before decode..."
						);
						try {
							await ctx.resume();
							console.log(
								"AudioPlayer: AudioContext resumed, proceeding with decode"
							);
						} catch (resumeError) {
							console.warn(
								"AudioPlayer: Could not resume before decode:",
								resumeError
							);
							// Continue anyway - might work or might need user gesture
						}
					}

					audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
					audioBufferRef.current = audioBuffer;
					console.log("AudioPlayer: Audio decode successful");
				} catch (decodeError) {
					console.error("AudioPlayer: Failed to decode audio:", decodeError);
					console.error("AudioPlayer: Decode error details:", {
						message: decodeError.message,
						name: decodeError.name,
						ctxState: ctx.state,
						loadImmediately,
					});

					// If AudioContext is suspended, try to resume and retry
					if (ctx.state === "suspended") {
						console.log(
							"AudioPlayer: AudioContext suspended during decode, attempting resume and retry..."
						);
						try {
							await ctx.resume();
							console.log(
								"AudioPlayer: AudioContext resumed, retrying decode..."
							);
							audioBuffer = await ctx.decodeAudioData(arrayBuffer);
							audioBufferRef.current = audioBuffer;
							console.log("AudioPlayer: Audio decode successful after resume");
							// Note: isLoadingWaveform will be set to false after waveform extraction
						} catch (resumeRetryError) {
							console.error(
								"AudioPlayer: Failed after resume retry:",
								resumeRetryError
							);
							// Continue with original error handling
						}
					}

					// If still no audioBuffer, try with a copy of the arrayBuffer
					if (!audioBuffer) {
						try {
							console.log(
								"AudioPlayer: Retrying decode with full arrayBuffer..."
							);
							audioBuffer = await ctx.decodeAudioData(arrayBuffer);
							audioBufferRef.current = audioBuffer;
							console.log("AudioPlayer: Audio decode successful on retry");
							// Note: isLoadingWaveform will be set to false after waveform extraction
						} catch (retryError) {
							console.error(
								"AudioPlayer: Failed to decode audio on retry:",
								retryError
							);
							// On iOS Safari, if decode fails due to suspended context,
							// we'll retry when user interacts (on play button click)
							if (loadImmediately && ctx.state === "suspended") {
								console.warn(
									"AudioPlayer: Decode failed due to suspended AudioContext on iOS. " +
										"Will retry when user clicks play button."
								);
								// Don't set error - allow user to trigger decode on play
								// But stop showing loading spinner - will retry on user interaction
								setIsLoadingWaveform(false);
								return;
							}
							setError(
								"Failed to load audio. Audio format may not be supported."
							);
							setIsLoadingWaveform(false); // Stop loading on error
							return;
						}
					}
				}

				console.log("AudioPlayer: Audio buffer decoded:", {
					duration: audioBuffer.duration,
					sampleRate: audioBuffer.sampleRate,
					numberOfChannels: audioBuffer.numberOfChannels,
					length: audioBuffer.length,
				});

				// Get duration from HTML5 audio (more reliable on iOS Safari)
				const html5Duration = await durationPromise;

				// CRITICAL: Ensure duration is a valid number
				// Try HTML5 duration first, then AudioBuffer duration, then calculated
				let audioDuration = 0;

				if (
					html5Duration &&
					isFinite(html5Duration) &&
					!isNaN(html5Duration) &&
					html5Duration > 0
				) {
					audioDuration = html5Duration;
					console.log(
						"AudioPlayer: Using HTML5 audio duration:",
						audioDuration
					);
				} else if (
					audioBuffer.duration &&
					isFinite(audioBuffer.duration) &&
					!isNaN(audioBuffer.duration) &&
					audioBuffer.duration > 0
				) {
					audioDuration = audioBuffer.duration;
					console.log(
						"AudioPlayer: Using AudioBuffer duration:",
						audioDuration
					);
				} else if (audioBuffer.length && audioBuffer.sampleRate) {
					audioDuration = audioBuffer.length / audioBuffer.sampleRate;
					console.log("AudioPlayer: Using calculated duration:", audioDuration);
				}

				if (
					audioDuration > 0 &&
					isFinite(audioDuration) &&
					!isNaN(audioDuration)
				) {
					setDuration(audioDuration);
					console.log(
						"AudioPlayer: Duration set to:",
						audioDuration,
						"seconds"
					);
				} else {
					console.error(
						"AudioPlayer: Could not determine duration, audioDuration:",
						audioDuration,
						"html5Duration:",
						html5Duration,
						"audioBuffer.duration:",
						audioBuffer?.duration
					);
					// Set to 0 to prevent NaN display
					setDuration(0);
				}

				// Create analyser for visualization
				if (!analyserRef.current) {
					analyserRef.current = ctx.createAnalyser();
					analyserRef.current.fftSize = 2048;
					analyserRef.current.smoothingTimeConstant = 0.8;
				}

				// Create gain node
				if (!gainNodeRef.current) {
					gainNodeRef.current = ctx.createGain();
					gainNodeRef.current.connect(ctx.destination);
				}

				// Extract static waveform (only if not provided from backend)
				if (
					waveformData &&
					Array.isArray(waveformData) &&
					waveformData.length === 1024
				) {
					console.log(
						"AudioPlayer: Skipping waveform extraction, using backend waveformData"
					);
					// Waveform already set from useEffect above
					setIsLoadingWaveform(false);
				} else {
					try {
						const samples = 1024;
						const channelData = audioBuffer.getChannelData(0);
						console.log(
							"AudioPlayer: Channel data length:",
							channelData.length
						);

						if (!channelData || channelData.length === 0) {
							console.error("AudioPlayer: Channel data is empty");
							// Create a default waveform
							const defaultWaveform = new Array(samples).fill(128);
							realWaveformRef.current = defaultWaveform;
							setAudioLevels(defaultWaveform);
							setIsLoadingWaveform(false); // Waveform loaded (even if default)
						} else {
							const blockSize = Math.floor(channelData.length / samples);
							console.log("AudioPlayer: Block size for waveform:", blockSize);

							const waveform = [];
							// Calculate RMS (root mean square) for each block to get better waveform representation
							// Also track global max to preserve relative differences between audio files
							let globalMax = 0;
							const rmsValues = [];

							for (let i = 0; i < samples; i++) {
								const startIdx = i * blockSize;
								const endIdx = Math.min(
									startIdx + blockSize,
									channelData.length
								);

								// Calculate RMS for this block
								let sumSquares = 0;
								let count = 0;
								for (let j = startIdx; j < endIdx; j++) {
									sumSquares += channelData[j] * channelData[j];
									count++;
								}
								const rms = count > 0 ? Math.sqrt(sumSquares / count) : 0;
								rmsValues.push(rms);
								globalMax = Math.max(globalMax, rms);
							}

							// Normalize using global max to preserve relative differences
							// This ensures each audio file's waveform looks unique
							const normalizeFactor = globalMax > 0 ? 127 / globalMax : 1;
							for (let i = 0; i < rmsValues.length; i++) {
								const normalized = Math.max(
									0,
									Math.min(255, 128 + rmsValues[i] * normalizeFactor)
								);
								waveform.push(normalized);
							}

							console.log(
								"AudioPlayer: Waveform extracted, length:",
								waveform.length,
								"first 5 values:",
								waveform.slice(0, 5),
								"last 5 values:",
								waveform.slice(-5)
							);
							realWaveformRef.current = waveform;
							console.log(
								"AudioPlayer: Setting audioLevels state with waveform"
							);
							setAudioLevels(waveform);
							setIsLoadingWaveform(false); // Waveform loaded successfully
							console.log("AudioPlayer: audioLevels state set successfully");
						}
					} catch (waveformError) {
						console.error(
							"AudioPlayer: Error extracting waveform:",
							waveformError
						);
						// Create a default waveform on error
						const defaultWaveform = new Array(1024).fill(128);
						realWaveformRef.current = defaultWaveform;
						setAudioLevels(defaultWaveform);
						setIsLoadingWaveform(false); // Waveform loaded (even if default/error)
					}
				}

				console.log(
					"AudioPlayer: Audio loaded successfully, duration:",
					audioBuffer.duration,
					"waveform length:",
					realWaveformRef.current?.length || 0
				);
			} catch (error) {
				console.error("AudioPlayer: Failed to load audio:", error);
				console.error("AudioPlayer: Error details:", {
					message: error.message,
					name: error.name,
					stack: error.stack,
				});
				// Set error state so user knows something went wrong
				setError("Unable to load audio waveform. Playback may still work.");
				setIsLoadingWaveform(false); // Stop loading on error
			}
		};

		if (audioUrl) {
			loadAudio();
		}

		return () => {
			// Cleanup
			if (sourceNodeRef.current) {
				try {
					sourceNodeRef.current.stop();
				} catch (e) {
					// Ignore errors when stopping
				}
				sourceNodeRef.current = null;
			}
			if (visualizationFrameRef.current) {
				cancelAnimationFrame(visualizationFrameRef.current);
				visualizationFrameRef.current = null;
			}
		};
	}, [audioUrl, isInViewport, loadImmediately, waveformData]);

	// Update currentTime while playing (separate from visualization)
	useEffect(() => {
		if (
			isPlaying &&
			duration > 0 &&
			audioContextRef.current &&
			startTimeRef.current
		) {
			let timeUpdateFrame = null;

			const updateTime = () => {
				if (!isPlaying || !audioContextRef.current || !startTimeRef.current) {
					if (timeUpdateFrame) {
						cancelAnimationFrame(timeUpdateFrame);
						timeUpdateFrame = null;
					}
					return;
				}

				const ctx = audioContextRef.current;
				const elapsed = ctx.currentTime - startTimeRef.current;
				const newTime = pausedTimeRef.current + elapsed;

				if (newTime >= duration) {
					// Audio finished
					setCurrentTime(duration);
					isPlayingRef.current = false;
					setIsPlaying(false);
					setCurrentTime(0);
					pausedTimeRef.current = 0;
					startTimeRef.current = 0;
					if (timeUpdateFrame) {
						cancelAnimationFrame(timeUpdateFrame);
						timeUpdateFrame = null;
					}
					if (showStaticWaveform && realWaveformRef.current) {
						setAudioLevels(realWaveformRef.current);
					}
					if (onEnded) {
						onEnded();
					}
				} else {
					setCurrentTime(Math.max(0, Math.min(newTime, duration)));
					timeUpdateFrame = requestAnimationFrame(updateTime);
				}
			};

			timeUpdateFrame = requestAnimationFrame(updateTime);

			return () => {
				if (timeUpdateFrame) {
					cancelAnimationFrame(timeUpdateFrame);
				}
			};
		}
	}, [isPlaying, duration, showStaticWaveform, onEnded]);

	// iOS-SAFE Visualization Effect
	useEffect(() => {
		if (!analyserRef.current) {
			return;
		}

		// Always stop existing loop
		if (visualizationFrameRef.current) {
			cancelAnimationFrame(visualizationFrameRef.current);
			visualizationFrameRef.current = null;
		}

		// Visualization should run ONLY while audio is actually playing
		const visualize = () => {
			const analyser = analyserRef.current;
			const source = sourceNodeRef.current;

			if (!analyser || !source) {
				// No source node means audio stopped - restore static waveform
				if (visualizationFrameRef.current) {
					cancelAnimationFrame(visualizationFrameRef.current);
					visualizationFrameRef.current = null;
				}
				if (!isPlayingRef.current && realWaveformRef.current) {
					setAudioLevels(realWaveformRef.current);
				}
				return;
			}

			// SAFARI FIX: Do not check React state — check actual playing state via ref
			if (!isPlayingRef.current) {
				if (visualizationFrameRef.current) {
					cancelAnimationFrame(visualizationFrameRef.current);
					visualizationFrameRef.current = null;
				}
				// Restore static waveform when stopped
				if (realWaveformRef.current) {
					setAudioLevels(realWaveformRef.current);
				}
				return;
			}

			try {
				const bufferLength = analyser.frequencyBinCount;
				const dataArray = new Uint8Array(bufferLength);
				analyser.getByteTimeDomainData(dataArray);

				// Detect if data is non-static (Safari returns all 128s when graph isn't connected)
				const waveform = Array.from(dataArray);
				if (waveform.some((v) => v !== 128)) {
					setAudioLevels(waveform);
				}
				// If all 128s, don't update (analyser might not be receiving data yet)

				visualizationFrameRef.current = requestAnimationFrame(visualize);
			} catch (visualizeError) {
				console.error("AudioPlayer: Visualization error:", visualizeError);
				if (visualizationFrameRef.current) {
					cancelAnimationFrame(visualizationFrameRef.current);
					visualizationFrameRef.current = null;
				}
				// Fallback to static waveform on error
				if (realWaveformRef.current) {
					setAudioLevels(realWaveformRef.current);
				}
			}
		};

		// SAFARI FIX: Start visualization when source node is created and playing
		// Use a polling approach since AudioBufferSourceNode doesn't have "playing" event
		// Check periodically if we should start visualization
		const checkAndStart = () => {
			if (
				isPlayingRef.current &&
				analyserRef.current &&
				sourceNodeRef.current &&
				!visualizationFrameRef.current
			) {
				console.log(
					"AudioPlayer: Starting visualization - source connected, analyser ready"
				);
				visualizationFrameRef.current = requestAnimationFrame(visualize);
			}
		};

		// Check immediately
		checkAndStart();

		// Also check periodically in case source node is created after this effect runs
		const intervalId = setInterval(checkAndStart, 50); // Reduced to 50ms for faster response

		// Show static waveform when not playing
		if (!isPlayingRef.current && isInViewport) {
			if (realWaveformRef.current && realWaveformRef.current.length > 0) {
				setAudioLevels(realWaveformRef.current);
			}
		}

		return () => {
			clearInterval(intervalId);
			if (visualizationFrameRef.current) {
				cancelAnimationFrame(visualizationFrameRef.current);
				visualizationFrameRef.current = null;
			}
		};
	}, [isInViewport, isPlaying]); // Added isPlaying to trigger when playback starts/stops

	// Clean up on unmount
	useEffect(() => {
		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
			if (visualizationFrameRef.current) {
				cancelAnimationFrame(visualizationFrameRef.current);
			}
			if (sourceNodeRef.current) {
				try {
					sourceNodeRef.current.stop();
				} catch (e) {
					// Ignore errors
				}
			}
		};
	}, []);

	// Format time helper
	const formatTime = (seconds) => {
		// Handle all invalid cases: NaN, Infinity, negative, or non-finite
		if (
			typeof seconds !== "number" ||
			isNaN(seconds) ||
			!isFinite(seconds) ||
			seconds < 0
		) {
			return "00:00";
		}
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};

	// Resume AudioContext on any user interaction (iOS Safari requirement)
	const resumeAudioContext = async () => {
		if (!audioContextRef.current) {
			// Create AudioContext if it doesn't exist
			audioContextRef.current = new (
				window.AudioContext || window.webkitAudioContext
			)();
		}

		const ctx = audioContextRef.current;
		// CRITICAL: Always try to resume (iOS Safari requirement)
		// Even if state is "running", calling resume() is safe and ensures it stays active
		try {
			if (ctx.state !== "running") {
				await ctx.resume();
				console.log(
					"AudioPlayer: AudioContext resumed on interaction, state:",
					ctx.state
				);
			}
			// Double-check it's running
			if (ctx.state !== "running") {
				console.warn(
					"AudioPlayer: AudioContext still not running after resume"
				);
				return false;
			}
			return true;
		} catch (resumeError) {
			console.error("AudioPlayer: Failed to resume AudioContext:", resumeError);
			return false;
		}
	};

	// Handle play/pause using Web Audio API
	const togglePlay = async (e) => {
		if (e) {
			e.stopPropagation();
			e.preventDefault();
		}

		// If audioBuffer not loaded but we have arrayBuffer, try to decode now (user gesture)
		if (
			!audioBufferRef.current &&
			arrayBufferRef.current &&
			audioContextRef.current
		) {
			console.log(
				"AudioPlayer: AudioBuffer not loaded, attempting decode on user gesture..."
			);
			try {
				const ctx = audioContextRef.current;
				// Ensure AudioContext is resumed (user gesture should allow this)
				if (ctx.state === "suspended") {
					await ctx.resume();
				}
				const decodedBuffer = await ctx.decodeAudioData(arrayBufferRef.current);
				audioBufferRef.current = decodedBuffer;
				console.log("AudioPlayer: Audio decode successful on user gesture");

				// Now extract waveform if not already done
				if (!realWaveformRef.current && decodedBuffer) {
					try {
						const samples = 1024;
						const channelData = decodedBuffer.getChannelData(0);
						if (channelData && channelData.length > 0) {
							const blockSize = Math.floor(channelData.length / samples);
							const waveform = [];
							let globalMax = 0;
							const rmsValues = [];

							for (let i = 0; i < samples; i++) {
								const startIdx = i * blockSize;
								const endIdx = Math.min(
									startIdx + blockSize,
									channelData.length
								);
								let sumSquares = 0;
								let count = 0;
								for (let j = startIdx; j < endIdx; j++) {
									sumSquares += channelData[j] * channelData[j];
									count++;
								}
								const rms = count > 0 ? Math.sqrt(sumSquares / count) : 0;
								rmsValues.push(rms);
								globalMax = Math.max(globalMax, rms);
							}

							const normalizeFactor = globalMax > 0 ? 127 / globalMax : 1;
							for (let i = 0; i < rmsValues.length; i++) {
								const normalized = Math.max(
									0,
									Math.min(255, 128 + rmsValues[i] * normalizeFactor)
								);
								waveform.push(normalized);
							}
							realWaveformRef.current = waveform;
							setAudioLevels(waveform);
							setIsLoadingWaveform(false); // Waveform loaded
							console.log("AudioPlayer: Waveform extracted on user gesture");
						}
					} catch (waveformError) {
						console.error(
							"AudioPlayer: Error extracting waveform on user gesture:",
							waveformError
						);
						setIsLoadingWaveform(false); // Stop loading on error
					}
				}
			} catch (decodeError) {
				console.error(
					"AudioPlayer: Failed to decode on user gesture:",
					decodeError
				);
				setError("Failed to load audio. Please try again.");
				return;
			}
		}

		if (!audioContextRef.current || !audioBufferRef.current) {
			console.warn("AudioPlayer: Audio not loaded");
			return;
		}

		const ctx = audioContextRef.current;
		const buffer = audioBufferRef.current;

		if (isPlaying) {
			// Pause
			if (sourceNodeRef.current) {
				try {
					sourceNodeRef.current.stop();
				} catch (stopError) {
					// Ignore errors
				}
				sourceNodeRef.current = null;
			}

			// Save current position
			const elapsed = ctx.currentTime - startTimeRef.current;
			pausedTimeRef.current += elapsed;
			startTimeRef.current = 0;

			isPlayingRef.current = false;
			setIsPlaying(false);
			if (showStaticWaveform && realWaveformRef.current) {
				setAudioLevels(realWaveformRef.current);
			}
		} else {
			// Play - CRITICAL: Must happen in user gesture handler for iOS Safari
			try {
				// CRITICAL: Resume AudioContext BEFORE playing (iOS Safari requirement)
				// Use the resumeAudioContext function which has better error handling
				const resumed = await resumeAudioContext();
				if (!resumed) {
					console.error("AudioPlayer: Failed to resume AudioContext");
					setError("Audio not ready. Please try clicking again.");
					return;
				}

				// Double-check AudioContext is running
				if (ctx.state !== "running") {
					console.warn(
						"AudioPlayer: AudioContext state is",
						ctx.state,
						"attempting to resume again"
					);
					try {
						await ctx.resume();
						if (ctx.state !== "running") {
							console.error(
								"AudioPlayer: AudioContext still not running after resume"
							);
							setError("Audio not ready. Please try clicking again.");
							return;
						}
					} catch (finalError) {
						console.error(
							"AudioPlayer: Final resume attempt failed:",
							finalError
						);
						setError("Audio not ready. Please try clicking again.");
						return;
					}
				}

				// Stop any existing source
				if (sourceNodeRef.current) {
					try {
						sourceNodeRef.current.stop();
					} catch (stopError) {
						// Ignore errors
					}
				}

				// Create new source node (required - can't reuse)
				const source = ctx.createBufferSource();
				source.buffer = buffer;

				// CRITICAL: Connect source -> analyser -> gain -> destination for visualization
				// Make sure analyser and gain nodes exist
				if (!analyserRef.current) {
					analyserRef.current = ctx.createAnalyser();
					analyserRef.current.fftSize = 2048;
					analyserRef.current.smoothingTimeConstant = 0.8;
				}
				if (!gainNodeRef.current) {
					gainNodeRef.current = ctx.createGain();
					gainNodeRef.current.connect(ctx.destination);
				}

				source.connect(analyserRef.current);
				analyserRef.current.connect(gainNodeRef.current);

				// Start playback from current position
				const offset = pausedTimeRef.current;
				source.start(0, offset);
				sourceNodeRef.current = source;

				// Track start time
				startTimeRef.current = ctx.currentTime;

				console.log(
					"AudioPlayer: About to set isPlaying=true, source connected to analyser",
					"analyser frequencyBinCount:",
					analyserRef.current.frequencyBinCount,
					"sourceNodeRef set:",
					!!sourceNodeRef.current,
					"analyser connected:",
					analyserRef.current.numberOfInputs > 0
				);

				isPlayingRef.current = true;
				setIsPlaying(true);

				// Trigger visualization immediately after setting refs
				// The effect will pick this up via isPlaying dependency, but also try immediate start
				setTimeout(() => {
					if (
						isPlayingRef.current &&
						analyserRef.current &&
						sourceNodeRef.current &&
						!visualizationFrameRef.current
					) {
						console.log(
							"AudioPlayer: Manually starting visualization after play"
						);
						// Start visualization loop
						const visualize = () => {
							if (
								!isPlayingRef.current ||
								!analyserRef.current ||
								!sourceNodeRef.current
							) {
								if (visualizationFrameRef.current) {
									cancelAnimationFrame(visualizationFrameRef.current);
									visualizationFrameRef.current = null;
								}
								if (!isPlayingRef.current && realWaveformRef.current) {
									setAudioLevels(realWaveformRef.current);
								}
								return;
							}

							try {
								const bufferLength = analyserRef.current.frequencyBinCount;
								const dataArray = new Uint8Array(bufferLength);
								analyserRef.current.getByteTimeDomainData(dataArray);
								const waveform = Array.from(dataArray);
								if (waveform.some((v) => v !== 128)) {
									setAudioLevels(waveform);
								}
								visualizationFrameRef.current =
									requestAnimationFrame(visualize);
							} catch (visualizeErr) {
								console.error(
									"AudioPlayer: Visualization error:",
									visualizeErr
								);
								if (visualizationFrameRef.current) {
									cancelAnimationFrame(visualizationFrameRef.current);
									visualizationFrameRef.current = null;
								}
							}
						};
						visualizationFrameRef.current = requestAnimationFrame(visualize);
					}
				}, 50); // Small delay to ensure everything is connected

				// Handle end of playback
				source.onended = () => {
					isPlayingRef.current = false;
					setIsPlaying(false);
					setCurrentTime(0);
					pausedTimeRef.current = 0;
					startTimeRef.current = 0;
					sourceNodeRef.current = null;
					if (showStaticWaveform && realWaveformRef.current) {
						setAudioLevels(realWaveformRef.current);
					}
					if (onEnded) {
						onEnded();
					}
				};

				console.log("AudioPlayer: Playback started at", offset, "seconds");
			} catch (error) {
				console.error("AudioPlayer: Error playing audio:", error);
				// Try to resume AudioContext if suspended
				if (ctx.state === "suspended") {
					try {
						await ctx.resume();
						// Retry play
						const source = ctx.createBufferSource();
						source.buffer = buffer;
						source.connect(analyserRef.current);
						analyserRef.current.connect(gainNodeRef.current);
						const offset = pausedTimeRef.current;
						source.start(0, offset);
						sourceNodeRef.current = source;
						startTimeRef.current = ctx.currentTime;
						isPlayingRef.current = true;
						setIsPlaying(true);
					} catch (retryError) {
						console.error("AudioPlayer: Retry failed:", retryError);
					}
				}
			}
		}
	};

	// Handle seek
	const handleSeek = async (seekEvent) => {
		if (!progressBarRef.current || !duration || !audioBufferRef.current) {
			return;
		}

		seekEvent.stopPropagation();
		const rect = progressBarRef.current.getBoundingClientRect();
		const x = seekEvent.clientX - rect.left;
		const percentage = x / rect.width;
		const newTime = Math.max(0, Math.min(percentage * duration, duration));

		if (isNaN(newTime)) {
			return;
		}

		const wasPlaying = isPlaying;
		const ctx = audioContextRef.current;

		// Stop current playback
		if (sourceNodeRef.current) {
			try {
				sourceNodeRef.current.stop();
			} catch (stopError) {
				// Ignore errors
			}
			sourceNodeRef.current = null;
		}

		// Update position
		pausedTimeRef.current = newTime;
		setCurrentTime(newTime);

		// Resume if was playing
		if (wasPlaying && ctx) {
			try {
				if (ctx.state === "suspended") {
					await ctx.resume();
				}

				const source = ctx.createBufferSource();
				source.buffer = audioBufferRef.current;
				source.connect(analyserRef.current);
				analyserRef.current.connect(gainNodeRef.current);
				source.start(0, newTime);
				sourceNodeRef.current = source;
				startTimeRef.current = ctx.currentTime;

				source.onended = () => {
					isPlayingRef.current = false;
					setIsPlaying(false);
					setCurrentTime(0);
					pausedTimeRef.current = 0;
					startTimeRef.current = 0;
					sourceNodeRef.current = null;
					if (showStaticWaveform && realWaveformRef.current) {
						setAudioLevels(realWaveformRef.current);
					}
					if (onEnded) {
						onEnded();
					}
				};
			} catch (error) {
				console.error("AudioPlayer: Error seeking:", error);
				setIsPlaying(false);
			}
		}
	};

	return (
		<div
			ref={containerRef}
			className="w-full"
			onClick={async (e) => {
				// Resume AudioContext on any click (iOS Safari requirement)
				await resumeAudioContext();
				if (onClick) {
					onClick(e);
				}
			}}
		>
			{/* Player UI */}
			<div
				className="w-full space-y-4 p-4"
				style={{
					backgroundColor: "rgba(0, 0, 0, 0.9)",
					borderColor: "rgba(234, 179, 8, 0.3)",
				}}
			>
				{error ? (
					<div className="mb-2 rounded bg-red-500/20 p-2 text-center text-sm text-red-400">
						{error}
					</div>
				) : null}
				{/* Waveform or Loading Spinner */}
				{isLoadingWaveform && audioLevels === null ? (
					<div
						className="flex min-h-[200px] items-center justify-center rounded-2xl border-2 p-4"
						style={{
							backgroundColor: "rgba(0, 0, 0, 0.9)",
							borderColor: "rgba(234, 179, 8, 0.3)",
						}}
					>
						<div className="text-center">
							<div
								className="mx-auto mb-3 h-12 w-12 animate-spin rounded-full border-4 border-gray-700 border-t-yellow-500"
								style={{ borderTopColor: "#fbbf24" }}
							/>
							<p className="text-sm text-gray-400">Loading waveform...</p>
						</div>
					</div>
				) : (
					<Waveform audioLevels={audioLevels} isRecording={false} />
				)}

				{/* Controls */}
				<div className="space-y-3">
					<div className="flex items-center gap-4">
						{/* Play/Pause Button */}
						<button
							onClick={async (e) => {
								e.stopPropagation(); // Prevent container onClick
								e.preventDefault();
								// CRITICAL: Resume AudioContext before playing (iOS Safari)
								const resumed = await resumeAudioContext();
								if (resumed) {
									// Small delay to ensure AudioContext is fully ready
									await new Promise((resolve) => {
										setTimeout(resolve, 50);
									});
									await togglePlay(e);
								} else {
									console.error(
										"AudioPlayer: Failed to resume AudioContext in button click"
									);
									setError("Audio not ready. Please try again.");
								}
							}}
							className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-2 shadow-lg transition-all"
							style={{
								borderColor: "#fbbf24",
								backgroundColor: "#fbbf24",
								boxShadow: isPlaying
									? "0 0 20px rgba(251, 191, 36, 0.6)"
									: "0 0 10px rgba(251, 191, 36, 0.3)",
							}}
							aria-label={isPlaying ? "Pause audio" : "Play audio"}
						>
							{isPlaying ? (
								<Pause size={20} fill="white" color="white" />
							) : (
								<Play size={20} fill="white" color="white" />
							)}
						</button>

						{/* Progress Bar */}
						<div className="flex-1">
							<div
								ref={progressBarRef}
								className="group relative h-2 cursor-pointer rounded-full bg-gray-700"
								onClick={handleSeek}
							>
								{/* Progress Fill */}
								<div
									ref={progressFillRef}
									className="absolute left-0 top-0 h-full rounded-full"
									style={{
										width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
										backgroundColor: "#fbbf24",
										boxShadow: "0 0 10px rgba(251, 191, 36, 0.6)",
									}}
								/>

								{/* Draggable Thumb */}
								<div
									ref={thumbRef}
									className="absolute h-4 w-4 cursor-grab rounded-full border-2 border-white shadow-lg active:cursor-grabbing group-hover:scale-110"
									style={{
										left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
										top: "50%",
										backgroundColor: "#fbbf24",
										boxShadow: "0 0 10px rgba(251, 191, 36, 0.8)",
										transform: "translate(-50%, -50%)",
										pointerEvents: "auto",
									}}
									onMouseDown={(mouseDownEvent) => {
										mouseDownEvent.stopPropagation();
										mouseDownEvent.preventDefault();

										const handleMouseMove = (moveEvent) => {
											if (!progressBarRef.current) {
												return;
											}

											const rect =
												progressBarRef.current.getBoundingClientRect();
											const x = Math.max(
												0,
												Math.min(moveEvent.clientX - rect.left, rect.width)
											);
											const percentage = x / rect.width;
											const newTime = percentage * duration;

											if (!isNaN(newTime) && duration > 0) {
												// Direct DOM update for instant feedback
												const percentValue = `${percentage * 100}%`;
												if (progressFillRef.current) {
													progressFillRef.current.style.width = percentValue;
												}
												if (thumbRef.current) {
													thumbRef.current.style.left = percentValue;
												}
												setCurrentTime(newTime);
											}
										};

										const handleMouseUp = async (upEvent) => {
											if (!progressBarRef.current) {
												return;
											}

											const rect =
												progressBarRef.current.getBoundingClientRect();
											const x = Math.max(
												0,
												Math.min(upEvent.clientX - rect.left, rect.width)
											);
											const percentage = x / rect.width;
											const newTime = percentage * duration;

											document.removeEventListener(
												"mousemove",
												handleMouseMove
											);
											document.removeEventListener("mouseup", handleMouseUp);

											if (!isNaN(newTime) && duration > 0) {
												await handleSeek({
													stopPropagation: () => {},
													clientX:
														rect.left + (newTime / duration) * rect.width,
												});
											}
										};

										document.addEventListener("mousemove", handleMouseMove);
										document.addEventListener("mouseup", handleMouseUp);
									}}
									onTouchStart={(touchStartEvent) => {
										touchStartEvent.stopPropagation();
										touchStartEvent.preventDefault();

										const handleTouchMove = (moveEvent) => {
											moveEvent.preventDefault();
											if (!progressBarRef.current) {
												return;
											}

											const [touch] = moveEvent.touches;
											const rect =
												progressBarRef.current.getBoundingClientRect();
											const x = Math.max(
												0,
												Math.min(touch.clientX - rect.left, rect.width)
											);
											const percentage = x / rect.width;
											const newTime = percentage * duration;

											if (!isNaN(newTime) && duration > 0) {
												// Direct DOM update for instant feedback
												const percentValue = `${percentage * 100}%`;
												if (progressFillRef.current) {
													progressFillRef.current.style.width = percentValue;
												}
												if (thumbRef.current) {
													thumbRef.current.style.left = percentValue;
												}
												setCurrentTime(newTime);
											}
										};

										const handleTouchEnd = async (endEvent) => {
											if (!progressBarRef.current) {
												return;
											}

											const rect =
												progressBarRef.current.getBoundingClientRect();
											const touch =
												endEvent.changedTouches?.[0] || endEvent.touches?.[0];
											if (!touch) {
												return;
											}

											const x = Math.max(
												0,
												Math.min(touch.clientX - rect.left, rect.width)
											);
											const percentage = x / rect.width;
											const newTime = percentage * duration;

											document.removeEventListener(
												"touchmove",
												handleTouchMove
											);
											document.removeEventListener("touchend", handleTouchEnd);

											if (!isNaN(newTime) && duration > 0) {
												await handleSeek({
													stopPropagation: () => {},
													clientX:
														rect.left + (newTime / duration) * rect.width,
												});
											}
										};

										document.addEventListener("touchmove", handleTouchMove, {
											passive: false,
										});
										document.addEventListener("touchend", handleTouchEnd);
									}}
								/>
							</div>
						</div>
					</div>

					{/* Duration Display */}
					<div
						className="flex w-full justify-between px-2 font-mono text-xs font-semibold"
						style={{ color: "#fbbf24" }}
					>
						<div>{formatTime(currentTime)}</div>
						<div>{formatTime(duration)}</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AudioPlayer;
